import { TranslatePipe } from '@myre/ui-primitive-core';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  inject,
  Input,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';

export const DEFAULT_STEP = 1;
export const DEFAULT_MIN = 0;
export const DEFAULT_MAX = 0;

export type SliderPipe = [{ transform: (...args: any[]) => any }, any[]?];

type TransformedValue = number | string;

const SLIDER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SliderComponent),
  multi: true,
};

@Component({
  selector: 'myre-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  providers: [SLIDER_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSlider, MatSliderThumb, TranslatePipe],
})
export class SliderComponent implements ControlValueAccessor {
  private readonly _changeDetectionRef = inject(ChangeDetectorRef);

  @HostBinding('class')
  public classes: string = 'myre-slider';

  @Input()
  public label: string;

  @Input()
  public set min(value: number) {
    this._min = value ?? DEFAULT_MIN;

    this._setTransformedMin();
  }

  public get min(): number {
    return this._min;
  }

  @Input()
  public set max(value: number) {
    this._max = value ?? DEFAULT_MAX;

    this._setTransformedMax();
  }

  public get max(): number {
    return this._max;
  }

  @Input()
  public step: number = DEFAULT_STEP;

  @Input()
  public readonly: boolean;

  @Input()
  public set pipes(value: SliderPipe[]) {
    if (value) {
      this._pipes = value;
      this._runAllPipes();
    }
  }

  public get pipes(): SliderPipe[] {
    return this._pipes;
  }

  @Input()
  public set valuePipes(value: SliderPipe[]) {
    if (value) {
      this._valuePipes = value;
      this._setTransformedValue();
    }
  }

  public get valuePipes(): SliderPipe[] {
    return this._valuePipes;
  }

  public transformedMax: TransformedValue = DEFAULT_MAX;
  public transformedMin: TransformedValue = DEFAULT_MIN;
  public transformedValue: TransformedValue;

  public disabled: boolean;

  public value: number | null;

  private _min: number = DEFAULT_MIN;

  private _max: number = DEFAULT_MAX;

  private _pipes: SliderPipe[] = [];
  private _valuePipes: SliderPipe[] = [];

  public get areMinMaxIndicatorsVisible(): boolean {
    return !this.readonly;
  }

  public writeValue(value: number): void {
    this._setCurrentValue(value);
    this._setTransformedValue();
    this._changeDetectionRef.detectChanges();
  }

  public registerOnChange(fn: <T>(value: T) => void): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public changeValue(value: number | null): void {
    this._setCurrentValue(value);
    this._setTransformedValue();
    this._onModelChange(value);
  }

  public setDisabledState(state: boolean): void {
    this.disabled = state;
  }

  public onTouched(): void {}

  private _setCurrentValue(value: number | null): void {
    this.value = value;
  }

  private _setTransformedValue(): void {
    this.transformedValue = this.valuePipes.length
      ? this._applyValuePipes()
      : this._applyPipes(this.value);
  }

  private _setTransformedMin(): void {
    this.transformedMin = this._applyPipes(this.min);
  }

  private _setTransformedMax(): void {
    this.transformedMax = this._applyPipes(this.max);
  }

  private _runAllPipes(): void {
    this._setTransformedValue();
    this._setTransformedMin();
    this._setTransformedMax();
  }

  private _applyPipes(initialValue: number): TransformedValue {
    return this._applyPipesHelper(this.pipes, initialValue);
  }

  private _applyValuePipes(): TransformedValue {
    return this._applyPipesHelper(this.valuePipes, this.value);
  }

  private _applyPipesHelper(
    pipes: SliderPipe[],
    initialValue: number,
  ): TransformedValue {
    return pipes.reduce((acc, next) => {
      const [pipe, params] = next;

      return pipe.transform(acc, ...(params || []));
    }, initialValue ?? 0);
  }

  private _onModelChange<T>(_: T): void {}
}
